/**
 * Which game a page plays, and what the player is told while the server
 * connection is not there (docs/deploy.md):
 *
 *   - `?server=ws://host:port` always plays on that server;
 *   - a build made with `VITE_GAME_SERVER=wss://host` (web/.env.example) has a
 *     Multiplayer button: it reloads the page into server mode on that server;
 *   - otherwise the page is the offline / legacy game, unchanged.
 *
 * There is no fallback from the server to the legacy game: an unreachable
 * server is retried and said to be unreachable, a refusal is shown with its
 * reason and what to do about it.
 */

/** sessionStorage: "the next load of this page joins the configured server" (set by the Multiplayer button, used once). */
const JOIN_KEY = 'opencraft.server.join';

interface Store { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

/** The production game server this build was made for ('' = none). */
export function configuredServer(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return (env?.VITE_GAME_SERVER ?? '').trim();
}

export interface ServerChoice {
  /** The server to play on; null = the offline / legacy game. */
  url: string | null;
  /** The player already asked to play (the Multiplayer button): skip the title. */
  autoplay: boolean;
  /** `url` is the build's configured server (not `?server=`). */
  configured: boolean;
}

/** Decide once, at page load. Consumes the Multiplayer button's request. */
export function chooseServer(search: string, configured = configuredServer(), store?: Store): ServerChoice {
  const q = new URLSearchParams(search).get('server');
  let asked = false;
  try {
    const st = store ?? sessionStorage;
    asked = st.getItem(JOIN_KEY) === '1';
    if (asked) st.removeItem(JOIN_KEY);
  } catch { /* storage unavailable */ }
  if (q) return { url: q, autoplay: false, configured: false };
  if (configured && asked) return { url: configured, autoplay: true, configured: true };
  return { url: null, autoplay: false, configured: false };
}

/** The Multiplayer button: come back as a server-mode page. `before` saves the offline game first. */
export function joinConfiguredServer(before?: () => void, store?: Store): void {
  try { (store ?? sessionStorage).setItem(JOIN_KEY, '1'); } catch { /* storage unavailable: the reload shows the title again */ }
  try { before?.(); } catch (e) { console.error('[srv] save before joining', e); }
  location.reload();
}

export type RefusalKind = 'full' | 'version' | 'duplicate' | 'kicked' | 'other';

export interface Refusal {
  kind: RefusalKind;
  /** What the player reads. */
  text: string;
  /** The button that gets them out of it. */
  action: 'retry' | 'reload';
  label: string;
}

/** Turn the server's reason (gameServer.ts login, host.ts limits) into something a player can act on. */
export function describeRefusal(why: string): Refusal {
  const w = (why || '').toLowerCase();
  if (w.includes('full')) return { kind: 'full', text: 'The server is full.\nTry again in a moment.', action: 'retry', label: 'Try again' };
  if (w.includes('protocol')) return { kind: 'version', text: 'This page is out of date: the server has been updated.\nReload the page to update.', action: 'reload', label: 'Reload' };
  if (w.includes('another location')) return { kind: 'duplicate', text: 'You logged in from another tab or device.\nThis one was disconnected.', action: 'retry', label: 'Play here instead' };
  if (w.includes('kicked')) return { kind: 'kicked', text: `You were disconnected by the server: ${why.replace(/^kicked:\s*/i, '')}.`, action: 'retry', label: 'Reconnect' };
  return { kind: 'other', text: `The server refused the connection: ${why || 'no reason given'}`, action: 'retry', label: 'Try again' };
}

/** One line for a connection that dropped or never opened. `retryMs` < 0: no further attempt. */
export function describeDrop(everOnline: boolean, attempt: number, retryMs: number, reason: string): string {
  const wait = retryMs >= 0 ? ` Retrying in ${Math.max(1, Math.round(retryMs / 1000))} s (attempt ${attempt}).` : '';
  if (/restart/i.test(reason)) return `The server is restarting.${wait}`;
  return (everOnline ? 'Connection lost.' : "Can't reach the server.") + wait;
}

export interface Notice {
  /** `buttons`: label -> what it does. */
  set(text: string, buttons?: [string, () => void][]): void;
  remove(): void;
}

/** A full-screen notice over the page: there is no world to show, or the one shown is no longer live. */
export function notice(): Notice {
  const el = document.createElement('div');
  el.id = 'srv-notice';
  el.style.cssText = 'position:fixed;inset:0;z-index:100000;display:flex;flex-direction:column;gap:14px;align-items:center;justify-content:center;'
    + 'background:rgba(16,16,18,0.92);color:#e0e0e0;font:16px monospace;text-align:center;padding:16px;white-space:pre-line';
  const msg = document.createElement('div');
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;justify-content:center';
  el.append(msg, row);
  let shown = false;
  return {
    set(text, buttons = []) {
      msg.textContent = text;
      row.replaceChildren(...buttons.map(([label, fn]) => {
        const b = document.createElement('button');
        b.textContent = label;
        b.style.cssText = 'font:16px monospace;color:#fff;background:#6f6f6f;border:2px solid #000;box-shadow:inset 2px 2px #aaa,inset -2px -2px #555;padding:8px 18px;cursor:pointer';
        b.addEventListener('click', fn);
        return b;
      }));
      if (!shown) { document.body.appendChild(el); shown = true; }
    },
    remove() { el.remove(); shown = false; },
  };
}

/** The buttons under a refusal: the way out of it, and the way back to the title (the offline game). */
export function refusalButtons(r: Refusal, configured: boolean): [string, () => void][] {
  const again = (): void => { if (configured) joinConfiguredServer(); else location.reload(); };
  const out: [string, () => void][] = [[r.label, r.action === 'reload' ? () => { if (configured) joinConfiguredServer(); else location.reload(); } : again]];
  if (configured) out.push(['Back to title', () => { location.reload(); }]);
  return out;
}
